import { Log } from '../AuxillaryService/Log';
import { EventEmitterHelper } from '../Utility/EventEmitter';
import { $ } from '../../vendor/jquery';
import { Zone } from '../Zone';

interface ConstructorConfig {
    id: number;
    height: number;
    width: number;
}

export class Display extends EventEmitterHelper {
    /**
     * Property - Display ID
     */
    readonly id: number;

    /**
     * Property - Display Height
     */
    readonly height: number;

    /**
     * Property - Display Width
     */
    readonly width: number;

    /**
     * Array - List of zones in display
     */
    readonly zones: Array<Zone> = [];

    /**
     * @see {@link Display.displayLoaded}
     */
    #displayLoaded = false;

    /**
     * Flag is true when display has fully loaded
     */
    get displayLoaded(): boolean {
        return this.#displayLoaded;
    }

    get wrapperElement(): HTMLElement {
        const wrapperElement = document.getElementById(`display_${this.id}`);
        if (!wrapperElement) {
            throw new Error(`No wrapperElement found for Display with id: ${this.id}`);
        }
        return wrapperElement;
    }

    constructor(config: ConstructorConfig) {
        super();

        this.id = config.id;
        this.height = config.height;
        this.width = config.width;
    }

    /**
     * Additional class construction to do after DOM elements have been created.
     */
    construct(): void {
        this.setEventEmitterElement(() => this.wrapperElement);
        this.eventEmitter.addListener('show-fullscreen-announcement', (evt: Event) => {
            void this._onFullscreenAnnouncement(evt);
        });
        this.eventEmitter.addListener('hide-fullscreen-announcement', (evt: Event) => {
            void this._onFullscreenAnnouncement(evt);
        });
    }

    /**
     * pauses all zones in display
     * @param exclusions - IDs of Zones to exclude from pausing
     */
    async pauseAllZones(exclusions: Array<number> = []): Promise<void> {
        const pausing = this.zones
            .filter((z) => exclusions.indexOf(z.id) === -1 && !z.isPaused)
            .map((z) => {
                return {
                    id: `zone:${z.id}:${z.name}`,
                    func: () => z.pause(),
                };
            });

        const failed = await failureReasons(pausing);
        if (failed.length > 0) {
            Log.Error(
                `Failed to pause ${failed.length} zones(s).\n  ${failed.join('\n  ')}`,
                null,
                __fileline
            );
        }
    }

    /**
     * stops all zones in this display
     * @param exclusions - IDs of Zones to exclude from stopping
     */
    async stopAllZones(exclusions: Array<number> = []): Promise<void> {
        const stopping = this.zones
            .filter((z) => exclusions.indexOf(z.id) === -1)
            .map((z) => {
                return {
                    id: `zone:${z.id}:${z.name}`,
                    func: () => z.stop(),
                };
            });

        const failed = await failureReasons(stopping);
        if (failed.length > 0) {
            Log.Error(
                `Failed to stop ${failed.length} zones(s).\n  ${failed.join('\n  ')}`,
                null,
                __fileline
            );
        }
    }

    /**
     * plays all zones in this display
     * @param exclusions - IDs of Zones to exclude from playing
     * @param cancelFade - Disable fading when loading all zones
     */
    async playAllZones(exclusions: Array<number> = [], cancelFade = false): Promise<void> {
        const playing = this.zones
            .filter((z) => exclusions.indexOf(z.id) === -1)
            .map((z) => {
                return {
                    id: `zone:${z.id}:${z.name}`,
                    func: () => z.play(cancelFade),
                };
            });

        const failed = await failureReasons(playing);
        if (failed.length > 0) {
            Log.Error(
                `Failed to play/resume ${failed.length} zones(s).\n  ${failed.join('\n  ')}`,
                null,
                __fileline
            );
        }
    }

    /**
     * @todo add description
     */
    scheduleCheckLoaded(): void {
        let zonesInitialized = 0;
        this.zones.forEach(function (z) {
            if (z.initialized) {
                zonesInitialized++;
            }
        });

        if (zonesInitialized == this.zones.length) {
            this.#displayLoaded = true;
        } else {
            setTimeout(() => {
                this.scheduleCheckLoaded();
            }, 1000);
        }
    }

    /**
     * @listens AnnouncementWidget#event:show-fullscreen-announcement
     * @listens AnnouncementWidget#event:hide-fullscreen-announcement
     */
    private async _onFullscreenAnnouncement(event: Event): Promise<void> {
        const exclusions: Array<number> = [];

        if (event.target instanceof Element) {
            const zone = $(event.target).parent('.zone').get(0);
            const matches = zone.id.match(/^zone_(\d+)$/);
            if (matches) {
                const zoneId = parseInt(matches[1], 10);
                exclusions.push(zoneId);
            }
        }

        // @todo clean this function up
        if (event.type === 'show-fullscreen-announcement') {
            await this.pauseAllZones(exclusions);
        } else if (event.type === 'hide-fullscreen-announcement') {
            await this.playAllZones(exclusions);
        }
    }
}

/**
 * Run a list of asynchronous functions in parallel and return the failure reasons of any rejected
 * promises.
 */
export async function failureReasons(
    entries: Array<{ id: string; func: () => Promise<unknown> }>
): Promise<Array<string>> {
    const ids = entries.map((e) => e.id);
    const fns = entries.map((e) => e.func());

    const results = await Promise.allSettled(fns);
    const failed = results
        .map((e, idx) =>
            e.status === 'rejected' ? `Reason (${ids[idx]}): ${String(e.reason)}` : ''
        )
        .filter((e) => {
            return e !== '';
        });

    return failed;
}
